import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  VStack,
  HStack,
  Spinner,
  Text,
  Heading,
  Alert,
  AlertIndicator,
  Link,
  Flex,
  Textarea,
} from "@chakra-ui/react";
import { BsBoxArrowRight } from "react-icons/bs";
import { format } from "date-fns";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { compareService, SpeciesComparison } from "../api/services/compare.service";
import { authService } from "../api/services/auth.service";

if (typeof document !== 'undefined') {
  const styleId = 'comparison-dialog-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .comparison-dialog-wrapper [role="dialog"],
      .comparison-dialog-wrapper [data-part="backdrop"],
      .comparison-dialog-wrapper [data-part="positioner"],
      .comparison-dialog-wrapper [data-part="content"] {
        z-index: 1400 !important;
      }
      .comparison-dialog-wrapper [data-part="backdrop"] {
        z-index: 1399 !important;
      }
      .comparison-dialog-wrapper [data-part="positioner"],
      .comparison-dialog-wrapper [data-part="content"] {
        z-index: 1401 !important;
      }
    `;
    document.head.appendChild(style);
  }
}

const DialogPositionerComponent = Dialog.Positioner as React.FC<any>;
const DialogContentComponent = Dialog.Content as React.FC<any>;
const DialogCloseTriggerComponent = Dialog.CloseTrigger as React.FC<any>;

type ComparisonButtonProps = {
  species1Id?: number;
  species2Id?: number;
  species1Name?: string;
  species2Name?: string;
  buttonLabel?: React.ReactNode;
  buttonProps?: ButtonProps;
  stopPropagation?: boolean;
};

type ComparisonContributeProps = {
  species1Id?: number;
  species2Id?: number;
  onClose: () => void;
};

function ComparisonContribute({ species1Id, species2Id, onClose }: ComparisonContributeProps) {
  const navigate = useNavigate();
  const loggedIn = authService.isAuthenticated();
  const [suggestion, setSuggestion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!species1Id || !species2Id) return null;

  const handleSubmit = async () => {
    const text = suggestion.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await compareService.submitCommunityComparison(species1Id, species2Id, text);
      setSubmitMessage("saved");
      setSuggestion("");
    } catch (err: any) {
      setSubmitError(err?.message || "Could not save your description.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <VStack align="stretch" gap={2} pt={4} borderTopWidth="1px">
      <Alert.Root status="warning">
        <AlertIndicator />
        <Text fontSize="sm">
          <FormattedMessage
            id="comparison_ai_disclaimer"
            defaultMessage="This text was generated by AI from several sources. It can contain mistakes."
          />
        </Text>
      </Alert.Root>
      <Text fontSize="sm" color="gray.700">
        <FormattedMessage
          id="comparison_suggest_note"
          defaultMessage="Birdr is a community project. If you know these birds, you can improve this comparison."
        />
      </Text>
      {loggedIn ? (
        submitMessage ? (
          <Text fontSize="sm" color="green.700" fontWeight="600">
            <FormattedMessage
              id="comparison_submitted"
              defaultMessage="Thanks — your description is live."
            />
          </Text>
        ) : (
          <>
            <Textarea
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              placeholder="How would you tell these two species apart?"
              minH="88px"
            />
            {submitError ? (
              <Text fontSize="sm" color="red.600">{submitError}</Text>
            ) : null}
            <Button
              colorPalette="primary"
              size="sm"
              alignSelf="flex-start"
              onClick={() => void handleSubmit()}
              disabled={!suggestion.trim() || submitting}
            >
              {submitting ? (
                <Spinner size="sm" />
              ) : (
                <FormattedMessage
                  id="comparison_suggest_better"
                  defaultMessage="Submit a better description"
                />
              )}
            </Button>
          </>
        )
      ) : (
        <Button
          variant="outline"
          colorPalette="primary"
          size="sm"
          alignSelf="flex-start"
          onClick={() => {
            onClose();
            navigate("/login");
          }}
        >
          <FormattedMessage
            id="comparison_login_to_suggest"
            defaultMessage="Log in to suggest a better description."
          />
        </Button>
      )}
    </VStack>
  );
}

export const ComparisonButton = ({
  species1Id,
  species2Id,
  species1Name,
  species2Name,
  buttonLabel = "Comparison",
  buttonProps,
  stopPropagation = false,
}: ComparisonButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [comparison, setComparison] = useState<SpeciesComparison | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    if (!species1Id || !species2Id) {
      return;
    }
    setIsOpen(true);
    setLoading(true);
    setError(null);
    try {
      const comparisonData = await compareService.getComparison(species1Id, species2Id);
      setComparison(comparisonData);
    } catch (err: any) {
      setError(err?.message || "Failed to generate comparison");
      setComparison(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (openState: boolean | { open: boolean }) => {
    const openValue = typeof openState === "boolean" ? openState : openState.open;
    if (!openValue) {
      setIsOpen(false);
      setError(null);
    } else {
      setIsOpen(true);
    }
  };

  const displayName1 = species1Name || comparison?.species_1_name || "";
  const displayName2 = species2Name || comparison?.species_2_name || "";

  return (
    <>
      <Button
        onClick={handleOpen}
        disabled={!species1Id || !species2Id || loading}
        colorPalette="primary"
        variant="subtle"
        size="sm"
        {...buttonProps}
      >
        {loading ? (
          <VStack gap={1}>
            <Spinner size="sm" />
            <Text fontSize="xs"><FormattedMessage id="loading" defaultMessage="Loading..." /></Text>
          </VStack>
        ) : (
          buttonLabel
        )}
      </Button>

      {isOpen && typeof document !== 'undefined' && createPortal(
        <Box className="comparison-dialog-wrapper">
          <Dialog.Root open={isOpen} onOpenChange={handleClose} modal={true}>
            <Dialog.Backdrop />
            <DialogPositionerComponent>
              <DialogContentComponent maxW="4xl" maxH="90vh" overflowY="auto">
                <Dialog.Header pr={10}>
                  <VStack align="start" gap={0}>
                    <Text fontWeight="bold" fontSize="lg">
                      {comparison
                        ? `${displayName1} vs ${displayName2}`
                        : "Species Comparison"}
                    </Text>
                    {comparison && (
                      <Text fontSize="sm" color="gray.500" fontStyle="italic">
                        {comparison.species_1_latin} / {comparison.species_2_latin}
                      </Text>
                    )}
                  </VStack>
                </Dialog.Header>
                <DialogCloseTriggerComponent 
                  position="absolute" 
                  top={3} 
                  right={3}
                  onClick={() => handleClose(false)}
                />
              <Dialog.Body>
                {loading ? (
                  <VStack gap={4} py={8}>
                    <Spinner size="lg" />
                    <Text>Generating comparison... This may take a moment.</Text>
                    <Text fontSize="sm" color="gray.600">
                      Scraping species data and generating AI comparison...
                    </Text>
                  </VStack>
                ) : error ? (
                  <VStack align="stretch" gap={4}>
                    <Alert.Root status="error">
                      <AlertIndicator />
                      <Box>
                        <Text fontWeight="bold">Error</Text>
                        <Text>{error}</Text>
                      </Box>
                    </Alert.Root>
                    <ComparisonContribute
                      species1Id={species1Id}
                      species2Id={species2Id}
                      onClose={() => handleClose(false)}
                    />
                  </VStack>
                ) : comparison ? (
                  <>
                    <style>{`
                      .comparison-html {
                        word-wrap: break-word;
                        overflow-wrap: break-word;
                      }
                      .comparison-html p { margin-bottom: 0.5rem; }
                      .comparison-html ul, .comparison-html ol {
                        list-style: none;
                        margin: 0;
                        padding: 0;
                        margin-bottom: 0.5rem;
                      }
                      .comparison-html li {
                        margin-bottom: 0.25rem;
                        padding-left: 0;
                      }
                      .comparison-html strong { font-weight: bold; }
                      .comparison-html em { font-style: italic; }
                      .comparison-html h1, .comparison-html h2, .comparison-html h3 { 
                        font-weight: bold; 
                        margin-top: 0.75rem; 
                        margin-bottom: 0.5rem; 
                      }
                    `}</style>

                    <HStack gap={4} mb={4} flexWrap="wrap">
                      <VStack align="start" gap={1}>
                        <Text fontWeight="600" fontSize="sm">{displayName1}</Text>
                        <HStack gap={3}>
                          <Link href={`https://ebird.org/species/${comparison.species_1_code}`} target="_blank" rel="noopener noreferrer">
                            <Flex gap={1} alignItems="center" fontSize="xs">
                              eBird <BsBoxArrowRight />
                            </Flex>
                          </Link>
                          <Link href={`https://birdsoftheworld.org/bow/species/${comparison.species_1_code}/cur/introduction`} target="_blank" rel="noopener noreferrer">
                            <Flex gap={1} alignItems="center" fontSize="xs">
                              Birds of the World <BsBoxArrowRight />
                            </Flex>
                          </Link>
                        </HStack>
                      </VStack>
                      <VStack align="start" gap={1}>
                        <Text fontWeight="600" fontSize="sm">{displayName2}</Text>
                        <HStack gap={3}>
                          <Link href={`https://ebird.org/species/${comparison.species_2_code}`} target="_blank" rel="noopener noreferrer">
                            <Flex gap={1} alignItems="center" fontSize="xs">
                              eBird <BsBoxArrowRight />
                            </Flex>
                          </Link>
                          <Link href={`https://birdsoftheworld.org/bow/species/${comparison.species_2_code}/cur/introduction`} target="_blank" rel="noopener noreferrer">
                            <Flex gap={1} alignItems="center" fontSize="xs">
                              Birds of the World <BsBoxArrowRight />
                            </Flex>
                          </Link>
                        </HStack>
                      </VStack>
                    </HStack>

                    <VStack align="stretch" gap={4}>
                      {comparison.summary_html && (
                        <Box>
                          <Heading size="md" mb={2}>
                            Summary
                          </Heading>
                          <Box
                            dangerouslySetInnerHTML={{ __html: comparison.summary_html }}
                            className="comparison-html"
                          />
                        </Box>
                      )}
                      {comparison.identification_tips_html && (
                        <Box bg="blue.50" p={4} borderRadius="md" borderWidth="1px" borderColor="blue.200">
                          <Heading size="sm" mb={2} color="blue.800">
                            Identification Tips
                          </Heading>
                          <Box
                            dangerouslySetInnerHTML={{ __html: comparison.identification_tips_html }}
                            className="comparison-html"
                            color="blue.900"
                          />
                        </Box>
                      )}

                      {comparison.size_comparison_html && (
                        <Box>
                          <Heading size="sm" mb={2}>
                            Size
                          </Heading>
                          <Box
                            dangerouslySetInnerHTML={{ __html: comparison.size_comparison_html }}
                            className="comparison-html"
                          />
                        </Box>
                      )}

                      {comparison.plumage_comparison_html && (
                        <Box>
                          <Heading size="sm" mb={2}>
                            Plumage
                          </Heading>
                          <Box
                            dangerouslySetInnerHTML={{ __html: comparison.plumage_comparison_html }}
                            className="comparison-html"
                          />
                        </Box>
                      )}

                      {comparison.behavior_comparison_html && (
                        <Box>
                          <Heading size="sm" mb={2}>
                            Behavior
                          </Heading>
                          <Box
                            dangerouslySetInnerHTML={{ __html: comparison.behavior_comparison_html }}
                            className="comparison-html"
                          />
                        </Box>
                      )}

                      {comparison.habitat_comparison_html && (
                        <Box>
                          <Heading size="sm" mb={2}>
                            Habitat
                          </Heading>
                          <Box
                            dangerouslySetInnerHTML={{ __html: comparison.habitat_comparison_html }}
                            className="comparison-html"
                          />
                        </Box>
                      )}

                      {comparison.vocalization_comparison_html && (
                        <Box>
                          <Heading size="sm" mb={2}>
                            Vocalization
                          </Heading>
                          <Box
                            dangerouslySetInnerHTML={{ __html: comparison.vocalization_comparison_html }}
                            className="comparison-html"
                          />
                        </Box>
                      )}

                      <Box pt={2} borderTopWidth="1px">
                        <Text fontSize="xs" color="gray.600">
                          Generated using {comparison.ai_model} on {format(new Date(comparison.generated_at), "PPpp")}
                        </Text>
                      </Box>
                    </VStack>
                    <ComparisonContribute
                      species1Id={species1Id}
                      species2Id={species2Id}
                      onClose={() => handleClose(false)}
                    />
                  </>
                ) : null}
              </Dialog.Body>
              <Dialog.Footer>
                <Button onClick={() => handleClose(false)} colorPalette="primary">
                  Close
                </Button>
              </Dialog.Footer>
            </DialogContentComponent>
          </DialogPositionerComponent>
        </Dialog.Root>
        </Box>,
        document.body
      )}
    </>
  );
};
